import { useEffect, useState } from 'react';
import { useAppSelector } from '../redux/hooks';
import type { CommuteMode } from '../redux/features/jobFormSlice';
import { fetchHomeCommute, type HomeCommuteDurations, type LatLng } from '../utils/map/fetch-home-commute';

export type HomeCommuteStatus = 'idle' | 'cached' | 'loading' | 'ready' | 'unavailable';

export interface HomeCommuteState {
  status: HomeCommuteStatus;
  /** Present for `cached` and `ready`. */
  durations: HomeCommuteDurations | null;
  /** True once the profile has a saved home address (the only case anything is ever fetched). */
  hasHome: boolean;
  homeLabel: string;
  preferredMode: CommuteMode | null;
}

const SESSION_CACHE_PREFIX = 'homeCommute:';

function cacheKey(from: LatLng, to: LatLng): string {
  return `${SESSION_CACHE_PREFIX}${from.lat.toFixed(4)},${from.lng.toFixed(4)}>${to.lat.toFixed(4)},${to.lng.toFixed(4)}`;
}

function readCache(key: string): HomeCommuteDurations | null {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as HomeCommuteDurations) : null;
  } catch {
    return null;
  }
}

function writeCache(key: string, value: HomeCommuteDurations): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode or full storage: the figure just gets fetched again next time */
  }
}

async function transitCommuteAvailable(signal: AbortSignal): Promise<boolean> {
  try {
    const res = await fetch('/api/commute-time/status', { signal });
    if (!res.ok) return false;
    return ((await res.json()) as { available?: boolean }).available === true;
  } catch {
    return false;
  }
}

/**
 * Travel times from the profile's saved home address to `jobPoint`. Nothing is ever fetched
 * on page load: the hook only reads this session's cache until `enabled` turns true (the job
 * page passes "the map modal is open"), and it does nothing at all for users without a saved
 * home address. Server render and first client paint are therefore identical (`idle`).
 */
export function useHomeCommute(jobPoint: LatLng | null, enabled: boolean): HomeCommuteState {
  const homeAddress = useAppSelector((state) => state.jobForm.homeAddress);
  const preferredMode = useAppSelector((state) => state.jobForm.preferredCommuteMode);
  const homeLat = homeAddress?.point?.lat;
  const homeLng = homeAddress?.point?.lng;
  const homeLabel = homeAddress?.label ?? '';
  const hasHome = typeof homeLat === 'number' && typeof homeLng === 'number';
  const jobLat = jobPoint?.lat;
  const jobLng = jobPoint?.lng;

  const [status, setStatus] = useState<HomeCommuteStatus>('idle');
  const [durations, setDurations] = useState<HomeCommuteDurations | null>(null);

  // Cache read: after mount only, so a cached figure appears without a server/client mismatch.
  useEffect(() => {
    if (!hasHome || typeof jobLat !== 'number' || typeof jobLng !== 'number') return;
    const cached = readCache(cacheKey({ lat: homeLat as number, lng: homeLng as number }, { lat: jobLat, lng: jobLng }));
    if (cached) {
      setDurations(cached);
      setStatus('cached');
    }
  }, [hasHome, homeLat, homeLng, jobLat, jobLng]);

  // Fetch: only once the map is open, and only if the cache didn't already answer.
  useEffect(() => {
    if (!enabled || !hasHome || typeof jobLat !== 'number' || typeof jobLng !== 'number') return undefined;
    const token = import.meta.env.PUBLIC_ENV__MAPBOX_ACCESS_TOKEN as string | undefined;
    if (!token) {
      setStatus('unavailable');
      return undefined;
    }
    const from = { lat: homeLat as number, lng: homeLng as number };
    const to = { lat: jobLat, lng: jobLng };
    const key = cacheKey(from, to);
    if (readCache(key)) {
      setStatus('ready');
      return undefined;
    }
    const controller = new AbortController();
    setStatus('loading');
    (async () => {
      const transitAvailable = await transitCommuteAvailable(controller.signal);
      const result = await fetchHomeCommute({ from, to, accessToken: token, transitAvailable, signal: controller.signal });
      if (controller.signal.aborted) return;
      writeCache(key, result);
      setDurations(result);
      setStatus(Object.keys(result).length > 0 ? 'ready' : 'unavailable');
    })().catch(() => {
      if (!controller.signal.aborted) setStatus('unavailable');
    });
    return () => controller.abort();
  }, [enabled, hasHome, homeLat, homeLng, jobLat, jobLng]);

  return { status, durations, hasHome, homeLabel, preferredMode };
}
